import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';
import { useStore } from '../../store/StoreContext';
import { getPriorityOptions } from '../../lib/constants';
import { useToast } from '../../hooks/useToast';
import ConfirmDialog from './ConfirmDialog';

// PriorityPicker — admin-editable picker for clients.priority. Three
// rendering variants share one piece of state-management logic:
//
//   variant="clients-row"  — Clients-page big block. White "PRIORITY" header
//                            band on top, slate-300 body below with the priority
//                            label in the priority's COLOR (text-colored, not
//                            saturated-bg). 110px wide, full row height.
//
//   variant="estimates-row"— Estimates-page mini block. Same idea but smaller
//                            (90px wide, smaller text).
//
//   variant="chip"         — Inline chip used in EstimateExpandedDetails.
//                            Saturated colored pill with white text + label.
//
// All variants render display-only when !editable. When editable, the trigger
// becomes a button that opens a portal'd dropdown menu (saturated colored
// option buttons stacked) anchored to the trigger's bounding rect. Selecting
// a tier:
//   - if client.priority is currently NULL (never explicitly set) → applies
//     without confirmation (this is the "first time setting" case)
//   - if client.priority is currently a non-null value → window.confirm
//     dialog: "Change [Client Name]'s priority from FOO to BAR?"
//
// Persists via useStore().saveClient. After a successful save, calls
// onChanged?.() so the parent can refresh local toast/state if needed.

const ENTER_MS = 180;
const EXIT_MS = 140;

function useTransitionedOpen(open) {
    const [mounted, setMounted] = useState(open);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (open) {
            setMounted(true);
            const id1 = requestAnimationFrame(() => {
                const id2 = requestAnimationFrame(() => setVisible(true));
                return () => cancelAnimationFrame(id2);
            });
            return () => cancelAnimationFrame(id1);
        }
        setVisible(false);
        const t = setTimeout(() => setMounted(false), EXIT_MS);
        return () => clearTimeout(t);
    }, [open]);
    return { mounted, visible };
}

export default function PriorityPicker({ client, editable, variant = 'clients-row', onChanged }) {
    const { show } = useToast();
    const { saveClient, config } = useStore();
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 150 });
    // Confirmation dialog state — replaces window.confirm. Set to the
    // target key when the user picks a different priority on an
    // already-set client; cleared when the dialog dismisses.
    const [pendingChange, setPendingChange] = useState(null); // { fromKey, toKey, fromLabel, toLabel }
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const { mounted, visible } = useTransitionedOpen(open);

    const options = useMemo(() => getPriorityOptions(config), [config?.priority_options]);
    const currentValue = client?.priority || null;
    const currentOpt = currentValue
        ? options.find(o => o.key === currentValue)
        : options.find(o => o.key === 'medium');
    const displayLabel = currentValue
        ? (currentOpt?.label || currentValue)
        : 'Medium';
    const displayColor = currentOpt?.color || '#3b82f6';

    // Recompute portal-menu position whenever it opens. Width matches the
    // trigger exactly — same behaviour as StatusDropdown's block size — so
    // the open menu reads as a continuation of the trigger column rather
    // than a wider floating element.
    useEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setMenuPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
        });
    }, [open]);

    // Click outside (counting both trigger AND portaled menu).
    useEffect(() => {
        if (!mounted) return;
        const handle = (e) => {
            if (triggerRef.current && triggerRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [mounted]);

    // ESC + scroll/resize close.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onChange = () => setOpen(false);
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onChange, true);
        window.addEventListener('resize', onChange);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onChange, true);
            window.removeEventListener('resize', onChange);
        };
    }, [open]);

    // Commit a priority change. Used by both the direct-set path (no
    // previous value → no confirmation) and by the confirmation modal
    // (after the user clicks OK).
    const applyPriority = async (newKey) => {
        if (!client?.id) return;
        try {
            await saveClient({ ...client, priority: newKey });
            onChanged?.(newKey);
        } catch (e) {
            show('Failed to update priority: ' + (e.message || 'Unknown error'), 'error');
        }
    };

    const handleSelect = (newKey) => {
        setOpen(false);
        if (!client?.id) return;
        if (newKey === currentValue) return;

        // First-time set (currentValue is null) → apply silently.
        // Changing an already-set value → open in-app confirmation modal.
        if (!currentValue) {
            applyPriority(newKey);
            return;
        }

        const newLabel = options.find(o => o.key === newKey)?.label || newKey;
        const oldLabel = currentOpt?.label || currentValue;
        setPendingChange({ fromKey: currentValue, toKey: newKey, fromLabel: oldLabel, toLabel: newLabel });
    };

    const confirmChange = async () => {
        if (!pendingChange) return;
        const { toKey } = pendingChange;
        setPendingChange(null);
        await applyPriority(toKey);
    };
    const cancelChange = () => setPendingChange(null);

    // ESC + click-outside also cancel the confirmation modal
    useEffect(() => {
        if (!pendingChange) return;
        const onKey = (e) => { if (e.key === 'Escape') cancelChange(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [pendingChange]);

    // ─── Trigger render — one of three visual variants ──────────────────
    const renderTrigger = () => {
        if (variant === 'chip') {
            return editable ? (
                <button ref={triggerRef} type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white cursor-pointer hover:opacity-90 focus:outline-none"
                    style={{ backgroundColor: displayColor }}>
                    {displayLabel}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg>
                </button>
            ) : (
                <span ref={triggerRef}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white"
                    style={{ backgroundColor: displayColor }}>
                    {displayLabel}
                </span>
            );
        }

        // clients-row vs estimates-row differ in:
        //   • chevron position — inline (right of text) on clients-row, so
        //     the wider 110px column stays balanced. Stacked below text on
        //     estimates-row, so longer labels (CRITICAL, etc.) get the full
        //     column width.
        //   • size scaling — clients-row text-base + small header padding;
        //     estimates-row matches the StatusDropdown block on the same row.
        const isMini = variant === 'estimates-row';
        const headerCls = 'flex-shrink-0 pt-1.5 pb-1 px-2 flex items-center justify-center bg-white';
        const headerSpanCls = 'font-lemonmilk text-[10px] uppercase leading-none whitespace-nowrap text-slate-900';
        // Estimates-row narrower column → text-sm; clients-row wider column → text-base
        const bodySpanCls = `font-lemonmilk uppercase leading-none whitespace-nowrap truncate text-center ${isMini ? 'text-sm' : 'text-base'}`;
        const wrapperBase = 'w-full h-full flex flex-col overflow-hidden';
        const chevronSize = isMini ? 12 : 12;

        const headerLabel = <div className={headerCls}>
            <span className={headerSpanCls} style={{ letterSpacing: '0.08em' }}>Priority</span>
        </div>;

        // Body layout: chevron stacked-below for the narrow Estimates row,
        // inline-right for the wider Clients row. Tighter padding on the
        // mini variant to give long labels (CRITICAL) max horizontal room.
        const bodyCls = isMini
            ? 'flex-1 flex flex-col items-center justify-center bg-slate-300 px-1.5 py-1 min-w-0 gap-0.5'
            : 'flex-1 flex items-center justify-center bg-slate-300 px-2 min-w-0 gap-1';

        const body = (
            <div className={bodyCls}>
                <span className={bodySpanCls + (isMini ? ' w-full' : '')} style={{ color: displayColor }}>
                    {displayLabel}
                </span>
                {editable && (
                    <svg width={chevronSize} height={chevronSize} viewBox="0 0 24 24" fill="none" stroke={displayColor} strokeWidth="3" className="flex-shrink-0">
                        <path d="M6 9l6 6 6-6"/>
                    </svg>
                )}
            </div>
        );

        return editable ? (
            <button ref={triggerRef} type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
                onMouseDown={(e) => e.stopPropagation()}
                className={`${wrapperBase} cursor-pointer hover:brightness-95 focus:outline-none text-left`}>
                {headerLabel}
                {body}
            </button>
        ) : (
            <div ref={triggerRef} className={wrapperBase}>
                {headerLabel}
                {body}
            </div>
        );
    };

    const menuStyle = {
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        zIndex: 1000,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(-4px) scale(0.96)',
        transformOrigin: 'top center',
        transition: visible
            ? `opacity ${ENTER_MS}ms var(--ease-out), transform ${ENTER_MS}ms var(--ease-out)`
            : `opacity ${EXIT_MS}ms var(--ease-out), transform ${EXIT_MS}ms var(--ease-out)`,
        pointerEvents: visible ? 'auto' : 'none',
    };

    return (
        <>
            {renderTrigger()}
            {mounted && editable && createPortal(
                <div ref={menuRef} role="listbox"
                    className="shadow-xl border border-slate-200 overflow-hidden flex flex-col"
                    style={menuStyle}>
                    {options.map(opt => {
                        const isCurrent = opt.key === currentValue;
                        return (
                            <button key={opt.key} type="button" role="option"
                                onClick={(e) => { e.stopPropagation(); handleSelect(opt.key); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="w-full px-1.5 py-2.5 font-lemonmilk text-xs text-center uppercase tracking-wide hover:opacity-90 transition-opacity truncate"
                                style={{ backgroundColor: opt.color, color: '#fff' }}>
                                {opt.label}{isCurrent && ' ✓'}
                            </button>
                        );
                    })}
                </div>,
                portalRoot(),
            )}
            <ConfirmDialog
                open={!!pendingChange}
                title={pendingChange ? `Change ${client?.name || 'this client'}'s priority to ${pendingChange.toLabel}?` : 'Change priority?'}
                confirmLabel={pendingChange ? `Change to ${pendingChange.toLabel}` : 'Change priority'}
                onConfirm={confirmChange}
                onCancel={cancelChange}>
                {pendingChange && (
                    <>
                        <span className="font-semibold text-slate-900">{client?.name || 'This client'}</span> moves from{' '}
                        <span className="font-bold uppercase" style={{ color: currentOpt?.color }}>{pendingChange.fromLabel}</span>{' '}
                        to{' '}
                        <span className="font-bold uppercase" style={{ color: options.find(o => o.key === pendingChange.toKey)?.color }}>{pendingChange.toLabel}</span>. Nothing else on the record changes; you can switch it back any time.
                    </>
                )}
            </ConfirmDialog>
        </>
    );
}
